import * as React from "react";
import useEventCallback from "@mui/utils/useEventCallback";
import AlertDialog, { AlertDialogPayload } from "./alert-dialog";
import ConfirmDialog, { ConfirmDialogPayload } from "./confirm-dialog";
import PromptDialog, { PromptDialogPayload } from "./prompt-dialog";

import { CloseDialog, DialogsContext, OpenDialog } from "./dialog-context";

// ---------- HOOK ----------

export interface DialogHook {
  alert(
    msg: React.ReactNode,
    options?: Partial<AlertDialogPayload>,
  ): Promise<void>;
  confirm(
    msg: React.ReactNode,
    options?: Partial<ConfirmDialogPayload>,
  ): Promise<boolean>;
  prompt(
    msg: React.ReactNode,
    options?: Partial<PromptDialogPayload>,
  ): Promise<string | null>;
  open: OpenDialog;
  close: CloseDialog;
}

export function useDialogs(): DialogHook {
  const ctx = React.useContext(DialogsContext);
  if (!ctx) {
    throw new Error("Dialogs context used without provider");
  }

  const { open, close } = ctx;

  const alert = useEventCallback(
    (msg: React.ReactNode, opts: Partial<AlertDialogPayload> = {}) =>
      open(AlertDialog, { ...opts, msg } as AlertDialogPayload),
  );

  const confirm = useEventCallback(
    (msg: React.ReactNode, opts: Partial<ConfirmDialogPayload> = {}) =>
      open(ConfirmDialog, { ...opts, msg } as ConfirmDialogPayload),
  );

  const prompt = useEventCallback(
    (msg: React.ReactNode, opts: Partial<PromptDialogPayload> = {}) =>
      open(PromptDialog, { ...opts, msg } as PromptDialogPayload),
  );

  return React.useMemo(
    () => ({
      alert,
      confirm,
      prompt,
      open,
      close,
    }),
    [alert, confirm, prompt, open, close],
  );
}
